// Classe pour la task associée à un module
import {
  type LearningModuleEntity,
  type LearningModuleInputEntity,
  LearningModuleInputSchema,
  LearningModuleSchema
} from '@/core/infrastructure/schemas/learning/module/LearningModuleSchema'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus'
import { LearningGrain } from '@/core/domain/learning/grain/LearningGrain'
import { ReferentialTask } from '@/core/domain/referential/task/ReferentialTask.ts'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator.ts'

export class LearningModule {
  id: number
  title: string
  status: LearningStatus.Values
  order: number
  task: ReferentialTask
  learningCourseId: number
  learningCourseName: string
  learningGrains: LearningGrain[]
  createdBy?: string
  createdAt?: Date
  modifiedBy?: string
  modifiedAt?: Date

  constructor(module: LearningModuleEntity) {
    this.id = module.id
    this.title = module.title
    this.status = module.status
    this.order = module.order

    this.task = ReferentialTask.create(module.task)

    this.learningCourseId = module.learningCourseId
    this.learningCourseName = module.learningCourseName

    // Conversion des grains
    this.learningGrains = module.learningGrains?.map((grain) => LearningGrain.create(grain)) ?? []

    this.createdBy = module.createdBy

    // Conversion des dates si elles existent
    if (module.createdAt) {
      this.createdAt = new Date(module.createdAt)
    }

    this.modifiedBy = module.modifiedBy

    if (module.modifiedAt) {
      this.modifiedAt = new Date(module.modifiedAt)
    }
  }

  // Retourne le nombre de grains du module
  grainsCount(): number {
    return this.learningGrains.length
  }

  // Retourne les grains actifs du module
  activeGrains(): LearningGrain[] {
    return this.learningGrains.filter((grain) => grain.status === LearningStatus.Values.ACTIVE)
  }

  // Obtenir les grains triés par ordre
  getSortedGrains(): LearningGrain[] {
    return [...this.learningGrains].sort((a, b) => a.order - b.order)
  }

  // Créer un objet d'entrée pour l'API
  createLearningModuleInput(): LearningModuleInput {
    return LearningModuleInput.create({
      id: this.id,
      title: this.title,
      status: this.status,
      order: this.order,
      taskId: this.task.id,
      learningGrains: this.learningGrains
    })
  }

  // Convertir vers le format d'API pour la mise à jour
  toApiFormat(): Record<string, any> {
    return {
      id: this.id,
      title: this.title,
      status: this.status,
      order: this.order,
      taskId: this.task.id,
      learningGrains: this.learningGrains.map((grain) => grain.toApiFormat())
    }
  }

  public static create(data: unknown): LearningModule {
    return EntityValidator.validateAndCreate(
      LearningModuleSchema,
      data,
      LearningModule,
      'LearningModule'
    )
  }
}

export class LearningModuleInput {
  id: number
  title: string
  status: LearningStatus.Values
  order: number
  taskId?: number
  learningGrains?: LearningGrain[]

  constructor(data: LearningModuleInputEntity) {
    this.id = data.id
    this.title = data.title
    this.status = data.status
    this.order = data.order
    this.taskId = data.taskId
    this.learningGrains = data.learningGrains?.map((grain) => LearningGrain.create(grain))
  }

  // Ajoute un nouveau grain vide au module
  addNewGrain(): LearningGrain {
    const grain = LearningGrain.createEmpty()

    this.learningGrains ??= []

    grain.order = this.learningGrains.length + 1

    this.learningGrains.push(grain)
    return grain
  }

  // Supprime un grain du module
  removeGrain(grain: LearningGrain): void {
    if (!this.learningGrains) return

    const index = this.learningGrains.findIndex((g) => g.order === grain.order)
    if (index !== -1) {
      this.learningGrains.splice(index, 1)
      // Réorganiser les ordres des grains restants
      this.learningGrains.forEach((g, i) => {
        g.order = i + 1
      })
    }
  }

  get hasGrains(): boolean {
    return !!this.learningGrains && this.learningGrains.length > 0
  }

  get canBePublished(): boolean {
    // At least one grain must be active
    const hasActiveGrain = this.learningGrains?.some((grain) => grain.isActive)
    // The module must have a task
    const hasTask = !!this.taskId
    // The module must have a title
    const hasTitle = !!this.title

    return !!(hasActiveGrain && hasTask && hasTitle)
  }

  get isActive(): boolean {
    return this.status === LearningStatus.Values.ACTIVE
  }

  get sortedLearningGrains(): LearningGrain[] {
    return this.learningGrains?.sort((a, b) => a.order - b.order) ?? []
  }

  public static create(data: unknown): LearningModuleInput {
    return EntityValidator.validateAndCreate(
      LearningModuleInputSchema,
      data,
      LearningModuleInput,
      'LearningModuleInput'
    )
  }
}
